import React, { useState, useRef, useEffect } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Fonts, MaterialShapes } from "@/constants/ThemeColours";

// Native audio imports from expo-audio (mocked in tests via mock-expo-audio.js)
let useAudioPlayer: any;
let useAudioPlayerStatus: any;

try {
  const expoAudio = require("expo-audio");
  useAudioPlayer = expoAudio.useAudioPlayer;
  useAudioPlayerStatus = expoAudio.useAudioPlayerStatus;
} catch {
  // Graceful fallback for non-native environments if module is missing
  useAudioPlayer = () => ({ play: () => {}, pause: () => {}, seekTo: () => {} });
  useAudioPlayerStatus = () => ({ playing: false, duration: 0, currentTime: 0 });
}

/**
 * Format raw seconds into standard mm:ss audio timestamp string.
 *
 * @param {number} totalSeconds - Audio duration or elapsed position in seconds.
 * @returns {string} Formatted timestamp (e.g. "0:45", "3:12").
 */
export function formatAudioTime(totalSeconds: number): string {
  if (!totalSeconds || isNaN(totalSeconds) || totalSeconds < 0) {
    return "0:00";
  }
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.floor(totalSeconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
}

/**
 * Properties for ThemedAudioPlayer component.
 */
export interface IThemedAudioPlayerProps {
  /** Audio media source URI or remote URL */
  url: string;
  /** Optional audio clip title or description */
  title?: string;
  /** Whether to render in compact inline mode (e.g. inside small cards) */
  compact?: boolean;
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
  /** Accessibility label */
  accessibilityLabel?: string;
  /** Test identifier */
  testID?: string;
}

/**
 * Native Audio Player sub-component powered by expo-audio.
 */
function NativeAudioPlayerCore({
  url,
  title,
  compact,
  style,
  testID,
}: IThemedAudioPlayerProps): React.JSX.Element {
  const player = useAudioPlayer(url);
  const status = useAudioPlayerStatus(player);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");
  const tintColor = useThemeColor({}, "button");
  const buttonTextColor = useThemeColor({}, "buttonText");

  const isPlaying = Boolean(status?.playing);
  const currentTime = status?.currentTime || 0;
  const duration = status?.duration || 0;
  const progressRatio = duration > 0 ? Math.min(Math.max(currentTime / duration, 0), 1) : 0;

  const handleTogglePlay = (): void => {
    if (!player) return;
    if (isPlaying) {
      player.pause();
    } else {
      player.play();
    }
  };

  const handleSeek = (ratio: number): void => {
    if (!player || duration <= 0) return;
    const targetSeconds = duration * ratio;
    if (typeof player.seekTo === "function") {
      player.seekTo(targetSeconds);
    }
  };

  return (
    <View
      style={[
        styles.playerCard,
        compact && styles.playerCardCompact,
        { backgroundColor: cardBg, borderColor },
        style,
      ]}
      testID={testID || "themed-audio-player"}
    >
      <View style={styles.topRow}>
        <TouchableOpacity
          onPress={handleTogglePlay}
          style={[styles.playButton, { backgroundColor: tintColor }]}
          activeOpacity={0.8}
          accessibilityLabel={isPlaying ? "Pause audio" : "Play audio"}
          accessibilityRole="button"
        >
          <Ionicons
            name={isPlaying ? "pause" : "play"}
            size={compact ? 16 : 22}
            color={buttonTextColor}
          />
        </TouchableOpacity>

        <View style={styles.infoCol}>
          <ThemedText
            numberOfLines={1}
            style={[styles.titleText, { color: textColor }]}
          >
            {title || "Audio Recording"}
          </ThemedText>

          <View style={styles.timeRow}>
            <ThemedText style={[styles.timeText, { color: textMuted }]}>
              {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
            </ThemedText>
            {isPlaying && (
              <ThemedText type="stamp" style={styles.playingBadge}>
                PLAYING
              </ThemedText>
            )}
          </View>
        </View>
      </View>

      {/* Progress Track */}
      <TouchableOpacity
        activeOpacity={1}
        onPress={(e) => {
          const { locationX } = e.nativeEvent as any;
          // approximate width 240
          if (locationX !== undefined) {
            handleSeek(Math.min(Math.max(locationX / 240, 0), 1));
          }
        }}
        style={[styles.progressTrack, { backgroundColor: borderColor }]}
        accessibilityLabel="Audio progress track"
        accessibilityRole="progressbar"
      >
        <View
          style={[
            styles.progressBar,
            { width: `${Math.round(progressRatio * 100)}%`, backgroundColor: tintColor },
          ]}
        />
      </TouchableOpacity>
    </View>
  );
}

/**
 * Web Audio Player sub-component using HTML5 audio with matching Archival Heirloom UI.
 */
function WebAudioPlayerCore({
  url,
  title,
  compact,
  style,
  testID,
}: IThemedAudioPlayerProps): React.JSX.Element {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");
  const tintColor = useThemeColor({}, "button");
  const buttonTextColor = useThemeColor({}, "buttonText");

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration || 0);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [url]);

  const handleTogglePlay = (): void => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((err) => console.warn("[ThemedAudioPlayer] Web playback error:", err));
    }
  };

  const progressRatio = duration > 0 ? Math.min(Math.max(currentTime / duration, 0), 1) : 0;

  return (
    <View
      style={[
        styles.playerCard,
        compact && styles.playerCardCompact,
        { backgroundColor: cardBg, borderColor },
        style,
      ]}
      testID={testID || "themed-audio-player"}
    >
      {/* Hidden audio element on Web */}
      {Platform.OS === "web" && (
        React.createElement("audio", {
          ref: audioRef,
          src: url,
          preload: "metadata",
          style: { display: "none" },
        })
      )}

      <View style={styles.topRow}>
        <TouchableOpacity
          onPress={handleTogglePlay}
          style={[styles.playButton, { backgroundColor: tintColor }]}
          activeOpacity={0.8}
          accessibilityLabel={isPlaying ? "Pause audio" : "Play audio"}
          accessibilityRole="button"
        >
          <Ionicons
            name={isPlaying ? "pause" : "play"}
            size={compact ? 16 : 22}
            color={buttonTextColor}
          />
        </TouchableOpacity>

        <View style={styles.infoCol}>
          <ThemedText
            numberOfLines={1}
            style={[styles.titleText, { color: textColor }]}
          >
            {title || "Audio Recording"}
          </ThemedText>

          <View style={styles.timeRow}>
            <ThemedText style={[styles.timeText, { color: textMuted }]}>
              {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
            </ThemedText>
            {isPlaying && (
              <ThemedText type="stamp" style={styles.playingBadge}>
                PLAYING
              </ThemedText>
            )}
          </View>
        </View>
      </View>

      {/* Progress Track */}
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={(e) => {
          const audio = audioRef.current;
          if (!audio || duration <= 0) return;
          const rect = (e.currentTarget as any)?.getBoundingClientRect?.();
          if (rect && rect.width > 0) {
            const clickX = (e.nativeEvent as any).clientX - rect.left;
            const ratio = Math.min(Math.max(clickX / rect.width, 0), 1);
            audio.currentTime = ratio * duration;
          }
        }}
        style={[styles.progressTrack, { backgroundColor: borderColor }]}
        accessibilityLabel="Audio progress track"
        accessibilityRole="progressbar"
      >
        <View
          style={[
            styles.progressBar,
            { width: `${Math.round(progressRatio * 100)}%`, backgroundColor: tintColor },
          ]}
        />
      </TouchableOpacity>
    </View>
  );
}

/**
 * Standardised Archival Cross-Platform Audio Player.
 * Renders consistent tactile audio playback controls across Android, iOS, and Web.
 *
 * @param {IThemedAudioPlayerProps} props - Player properties.
 * @returns {React.JSX.Element} The rendered audio player.
 */
export function ThemedAudioPlayer(props: IThemedAudioPlayerProps): React.JSX.Element {
  if (Platform.OS === "web") {
    return <WebAudioPlayerCore {...props} />;
  }
  return <NativeAudioPlayerCore {...props} />;
}

export default ThemedAudioPlayer;

const styles = StyleSheet.create({
  playerCard: {
    width: "100%",
    maxWidth: 420,
    borderWidth: 1,
    borderRadius: MaterialShapes.small,
    padding: 12,
    marginVertical: 6,
    alignSelf: "center",
  },
  playerCardCompact: {
    padding: 8,
    marginVertical: 2,
    maxWidth: 320,
  },
  topRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  playButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
  },
  infoCol: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  titleText: {
    fontSize: 13,
    fontWeight: "600",
    fontFamily: Fonts.sans,
  },
  timeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  timeText: {
    fontSize: 11,
    fontFamily: Fonts.mono,
  },
  playingBadge: {
    fontSize: 9,
    color: "#2173DF",
    letterSpacing: 0.8,
  },
  progressTrack: {
    height: 6,
    width: "100%",
    borderRadius: 3,
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    borderRadius: 3,
  },
});
